import { Navigate, Route, Routes } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { defaultAdminPath, PulseShell } from '@/components/pulse-shell';
import { OverviewPage } from '@/components/pages/overview';
import { SessionsPage } from '@/components/pages/sessions';
import { SessionDetailPage } from '@/components/pages/session-detail';
import { SessionPerceptionPage } from '@/components/pages/session-perception';
import { AgentsPage } from '@/components/pages/agents';
import { PerceptionPage } from '@/components/pages/perception';
import { RecordingsPage } from '@/components/pages/recordings';
import { SettingsPage } from '@/components/pages/settings';
import { CheckupsPage } from '@/components/pages/checkups';
import { CheckupDetailPage } from '@/components/pages/checkup-detail';
import { IncidentsPage } from '@/components/pages/incidents';
import { IncidentDetailPage } from '@/components/pages/incident-detail';
import { AuditPage } from '@/components/pages/audit';
import { AuditDetailPage } from '@/components/pages/audit-detail';
import { LoginPage } from '@/components/pages/login';
import { UsersAllPage } from '@/components/pages/users-all';
import { UsersRolesPage } from '@/components/pages/users-roles';
import { UsersRoleEditPage } from '@/components/pages/users-role-edit';
import { UserSettingsPage } from '@/components/pages/user-settings';
import { InvitePage } from '@/components/pages/invite';
import { ResetPasswordPage } from '@/components/pages/reset-password';
import { AuthProfileProvider, useAuthProfile } from '@/lib/auth-context';
import { RequirePermission } from '@/components/require-permission';
import { ensureAdminTokenFresh, getAuthProfile, type AuthProfile } from '@/lib/auth';

function HomeRedirect() {
  const profile = useAuthProfile();
  return <Navigate to={defaultAdminPath(profile?.permissions)} replace />;
}

function Protected({ children }: { children: React.ReactNode }) {
  const [profile, setProfile] = useState<AuthProfile | null | undefined>(undefined);
  useEffect(() => {
    void getAuthProfile().then(setProfile);
  }, []);

  // Tab left open overnight: refresh Cognito when the user comes back.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void ensureAdminTokenFresh().then((token) => {
        if (!token) setProfile(null);
      });
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  if (profile === undefined) return <div style={{ padding: 40 }}>Loading…</div>;
  // Prefer platform RBAC (/admin/me). Cognito admin + transitional permissions cover deploy gaps.
  if (!profile?.isAdmin) {
    return <Navigate to="/login" replace />;
  }
  return (
    <AuthProfileProvider profile={profile}>
      <PulseShell>{children}</PulseShell>
    </AuthProfileProvider>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invite" element={<InvitePage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/" element={<Protected><HomeRedirect /></Protected>} />
      <Route path="/overview" element={<Protected><RequirePermission permission="dashboard.view" title="Dashboard" message="You need the dashboard.view permission to open the Dashboard."><OverviewPage /></RequirePermission></Protected>} />
      <Route path="/sessions" element={<Protected><RequirePermission permission="sessions.view" title="Sessions" message="You need the sessions.view permission to open Sessions."><SessionsPage /></RequirePermission></Protected>} />
      <Route path="/sessions/:id/perception" element={<Protected><RequirePermission permission="sessions.view_perception" title="Session perception" message="You need the sessions.view_perception permission."><SessionPerceptionPage /></RequirePermission></Protected>} />
      <Route path="/sessions/:id" element={<Protected><RequirePermission permission="sessions.view" title="Sessions" message="You need the sessions.view permission to open Sessions."><SessionDetailPage /></RequirePermission></Protected>} />
      <Route path="/agents" element={<Protected><RequirePermission permission="agents.view" title="Agents" message="You need the agents.view permission to open Agents."><AgentsPage /></RequirePermission></Protected>} />
      <Route path="/perception" element={<Protected><RequirePermission permission="perception.view" title="Perception" message="You need the perception.view permission."><PerceptionPage /></RequirePermission></Protected>} />
      <Route path="/recordings" element={<Protected><RequirePermission permission="recordings.view" title="Recordings" message="You need the recordings.view permission."><RecordingsPage /></RequirePermission></Protected>} />
      <Route path="/users" element={<Navigate to="/users/all" replace />} />
      <Route path="/users/all" element={<Protected><RequirePermission permission="users.view" title="Users" message="You need the users.view permission to open User management."><UsersAllPage /></RequirePermission></Protected>} />
      <Route path="/users/roles" element={<Protected><RequirePermission permission="users.view" title="Roles" message="You need the users.view permission to open Roles."><UsersRolesPage /></RequirePermission></Protected>} />
      <Route path="/users/roles/:roleId" element={<Protected><RequirePermission permission="users.view" title="Roles" message="You need the users.view permission to open Roles."><UsersRoleEditPage /></RequirePermission></Protected>} />
      <Route path="/account" element={<Protected><RequirePermission permission="account.view" title="User settings" message="You need the account.view permission to open User settings."><UserSettingsPage /></RequirePermission></Protected>} />
      <Route path="/settings" element={<Navigate to="/settings/general" replace />} />
      <Route path="/settings/:tab" element={<Protected><RequirePermission permission="settings.view" title="Settings" message="You need the settings.view permission."><SettingsPage /></RequirePermission></Protected>} />
      <Route path="/checkups" element={<Protected><RequirePermission permission="checkups.view" title="Checkups" message="You need the checkups.view permission."><CheckupsPage /></RequirePermission></Protected>} />
      <Route path="/checkups/:id" element={<Protected><RequirePermission permission="checkups.view" title="Checkups" message="You need the checkups.view permission."><CheckupDetailPage /></RequirePermission></Protected>} />
      <Route path="/incidents" element={<Protected><RequirePermission permission="incidents.view" title="Incidents" message="You need the incidents.view permission."><IncidentsPage /></RequirePermission></Protected>} />
      <Route path="/incidents/:id" element={<Protected><RequirePermission permission="incidents.view" title="Incidents" message="You need the incidents.view permission."><IncidentDetailPage /></RequirePermission></Protected>} />
      <Route path="/audit" element={<Protected><RequirePermission permission="audit.view" title="Audit" message="You need the audit.view permission."><AuditPage /></RequirePermission></Protected>} />
      <Route path="/audit/:id" element={<Protected><RequirePermission permission="audit.view" title="Audit" message="You need the audit.view permission."><AuditDetailPage /></RequirePermission></Protected>} />
      <Route path="*" element={<Protected><HomeRedirect /></Protected>} />
    </Routes>
  );
}
